import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ResetPassword from '@/pages/ResetPassword';
import ReadinessAssessment from './pages/ReadinessAssessment';
import ReadinessAssessmentWizard from './pages/ReadinessAssessmentWizard';
import ReadinessAssessmentDashboard from './pages/ReadinessAssessmentDashboard';
import { ROUTES } from './config/routes';
import { AssessmentProvider } from './contexts/AssessmentContext';
import Index from './pages/Index';
import AssessmentSteps from './features/LegacyBenchmarksUI/components/AssessmentSteps';
import PermitBenchmarking from './features/LegacyBenchmarksUI/pages/PermitBenchmarking';
import PermitBenchmarkingWizard from './features/LegacyBenchmarksUI/pages/PermitBenchmarkingWizard';
import { LayoutProvider } from './contexts/LayoutContext';
import PasswordRecoveryHandler from './components/PasswordRecoveryHandler';
import { NewBenchmarksUI } from '@/features/NewBenchmarksUI/NewBenchmarksUI';
import { useEffect, useState } from 'react';
import { FeatureFlagsProvider, useFeatureFlags, useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/shared/sonner';
import SharedOpportunityAssessment from './pages/SharedOpportunityAssessment';


declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitializing, setIsInitializing] = useState(true);

  // Set initialization to false after a short delay to allow contexts to load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!gaId) return;

    // Define shim BEFORE loading the script
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function(...args) {
      window.dataLayer.push(args);
    };

    // Set consent BEFORE config
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'granted',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    // Queue boot + config immediately
    window.gtag('js', new Date());
    window.gtag('config', gaId, {
      debug_mode: !import.meta.env.PROD,
      site_name: 'PermitLab',
      app_name: 'PermitLab',
    });

    // Load the lib AFTER the shim is in place
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    // Track SPA route changes
    const handleRouteChange = (path) => {
      window.gtag('config', gaId, {
        page_path: path,
      });
    };

    // Listen for route changes
    return () => {
      // cleanup if needed
    };
  }, [navigate, location.pathname]);

  useEffect(() => {
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!import.meta.env.PROD || !gaId || !window.gtag) return;

    window.gtag('config', gaId, {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
      site_name: 'PermitLab',
      app_name: 'PermitLab',
    });
  }, [location.pathname, location.search]);

  // Show loading state while initializing
  if (isInitializing) {
    console.log('[App] Still initializing...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  console.log('[App] Initialization complete, rendering main app');

  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

// Separate component that uses auth context
function AppWithAuth() {
  const { session } = useAuth();
  
  return (
    <FeatureFlagsProvider session={session}>
      <LayoutProvider>
        <PasswordRecoveryHandler />
        <AppRoutes />
        <Toaster />
      </LayoutProvider>
    </FeatureFlagsProvider>
  );
}

// Simple catch-all component that redirects to benchmarking
function SimpleCatchAll() {
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log('[SimpleCatchAll] Unknown route - redirecting to benchmarking');
    navigate(ROUTES.PERMIT_BENCHMARKING.ROOT, { replace: true });
  }, [navigate]);
  
  return null;
}

// Separate component that uses feature flags (must be inside FeatureFlagsProvider)
function AppRoutes() {
  console.log('[AppRoutes] Rendering routes...');
  
  return (
    <Routes>
      {/* Handle password recovery links that land on root with code parameter */}
      <Route
        path={ROUTES.HOME}
        element={
          <PasswordRecoveryRedirect />
        }
      />

      {/* Keep the old landing page accessible directly */}
      <Route path="/landing" element={<MainLayout><Landing /></MainLayout>} />

      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path={ROUTES.SIGNUP} element={<SignUp />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
      
      {/* Redirect from /readiness-assessment to the proper route */}
      <Route 
        path="/readiness-assessment" 
        element={<Navigate to={ROUTES.READINESS_ASSESSMENT.ROOT} replace />} 
      />
      
      {/* Shared Assessment routes */}
      <Route path={ROUTES.SHARED.OPPORTUNITY} element={
        <MainLayout>
          <SharedOpportunityAssessment />
        </MainLayout>
      } />
      
      {/* Opportunity Assessment routes with AssessmentProvider - always render, handle feature flag inside */}
      <Route path={ROUTES.OPPORTUNITY_ASSESSMENT.ROOT} element={
        <OpportunityAssessmentWrapper>
          <AssessmentProvider>
            <MainLayout>
              <AssessmentSteps />
            </MainLayout>
          </AssessmentProvider>
        </OpportunityAssessmentWrapper>
      } />
      <Route path={ROUTES.OPPORTUNITY_ASSESSMENT.WIZARD} element={
        <OpportunityAssessmentWrapper>
          <AssessmentProvider>
            <MainLayout>
              <AssessmentSteps />
            </MainLayout>
          </AssessmentProvider>
        </OpportunityAssessmentWrapper>
      } />
      
      {/* Readiness Assessment routes with AssessmentProvider - always render, handle feature flag inside */}
      <Route path={ROUTES.READINESS_ASSESSMENT.ROOT} element={
        <ReadinessAssessmentWrapper>
          <AssessmentProvider>
            <MainLayout><ReadinessAssessment /></MainLayout>
          </AssessmentProvider>
        </ReadinessAssessmentWrapper>
      } />
      <Route path={ROUTES.READINESS_ASSESSMENT.WIZARD} element={
        <ReadinessAssessmentWrapper>
          <AssessmentProvider>
            <MainLayout><ReadinessAssessmentWizard /></MainLayout>
          </AssessmentProvider>
        </ReadinessAssessmentWrapper>
      } />
      <Route path={ROUTES.READINESS_ASSESSMENT.DASHBOARD} element={
        <ReadinessAssessmentWrapper>
          <AssessmentProvider>
            <MainLayout><ReadinessAssessmentDashboard /></MainLayout>
          </AssessmentProvider>
        </ReadinessAssessmentWrapper>
      } />

      {/* Permit Benchmarking routes */}
      <Route path={ROUTES.PERMIT_BENCHMARKING.ROOT} element={
        <MainLayout>
          <NewBenchmarksUI />
        </MainLayout>
      } />
      <Route path={ROUTES.PERMIT_BENCHMARKING.WIZARD} element={
        <MainLayout>
          <PermitBenchmarkingWizard />
        </MainLayout>
      } />
      {/* Legacy Benchmarking UI (internal only) */}
      <Route path={ROUTES.PERMIT_BENCHMARKING.LEGACY} element={
        <MainLayout>
          <PermitBenchmarking />
        </MainLayout>
      } />

      {/* Simple catch-all that redirects to benchmarking */}
      <Route path="*" element={<SimpleCatchAll />} />
    </Routes>
  );
}

// Wrapper components that check feature flags
function OpportunityAssessmentWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { showOpportunityAssessment } = useFeatureFlags();
  const { isLoading } = useFeatureFlagsContext();
  
  console.log('[OpportunityAssessmentWrapper] Render:', { 
    showOpportunityAssessment, 
    isLoading, 
    pathname: window.location.pathname 
  });
  
  useEffect(() => {
    console.log('[OpportunityAssessmentWrapper] useEffect:', { 
      showOpportunityAssessment, 
      isLoading 
    });
    
    // Only redirect if we're not loading AND the feature is explicitly disabled
    if (!isLoading && showOpportunityAssessment === false) {
      console.log('[OpportunityAssessmentWrapper] Feature explicitly disabled, redirecting to benchmarking');
      navigate(ROUTES.PERMIT_BENCHMARKING.ROOT, { replace: true });
    }
  }, [navigate, showOpportunityAssessment, isLoading]);
  
  // Always show loading while feature flags are loading
  if (isLoading) {
    console.log('[OpportunityAssessmentWrapper] Feature flags still loading, showing spinner');
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If feature is explicitly disabled and not loading, show loading while redirecting
  if (showOpportunityAssessment === false) {
    console.log('[OpportunityAssessmentWrapper] Feature explicitly disabled, showing loading while redirecting');
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  console.log('[OpportunityAssessmentWrapper] Feature enabled, rendering children');
  return <>{children}</>;
}

function ReadinessAssessmentWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { showReadinessAssessment } = useFeatureFlags();
  const { isLoading } = useFeatureFlagsContext();
  
  console.log('[ReadinessAssessmentWrapper] Render:', { 
    showReadinessAssessment, 
    isLoading, 
    pathname: window.location.pathname 
  });
  
  useEffect(() => {
    console.log('[ReadinessAssessmentWrapper] useEffect:', { 
      showReadinessAssessment, 
      isLoading 
    });
    
    // Only redirect if we're not loading AND the feature is explicitly disabled
    if (!isLoading && showReadinessAssessment === false) {
      console.log('[ReadinessAssessmentWrapper] Feature explicitly disabled, redirecting to benchmarking');
      navigate(ROUTES.PERMIT_BENCHMARKING.ROOT, { replace: true });
    }
  }, [navigate, showReadinessAssessment, isLoading]);
  
  // Always show loading while feature flags are loading
  if (isLoading) {
    console.log('[ReadinessAssessmentWrapper] Feature flags still loading, showing spinner');
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If feature is explicitly disabled and not loading, show loading while redirecting
  if (showReadinessAssessment === false) {
    console.log('[ReadinessAssessmentWrapper] Feature explicitly disabled, showing loading while redirecting');
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  console.log('[ReadinessAssessmentWrapper] Feature enabled, rendering children');
  return <>{children}</>;
}

// Component to handle password recovery redirects from root path
function PasswordRecoveryRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  
  console.log('[PasswordRecoveryRedirect] Component mounted, location:', location.pathname);
  
  useEffect(() => {
    // Check if this is a password recovery link
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.substring(1));
    
    // Check for both 'code' and 'token' parameters (Supabase uses 'token' for PKCE flows)
    const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
    const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
    const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                           (hashParams.has('type') && hashParams.get('type') === 'recovery');
    
    // Additional check for any parameters that might indicate a recovery flow
    // Supabase sometimes sends additional parameters like 'next', 'error', etc.
    const hasAnyParams = urlParams.toString() || hashParams.toString();
    const isRecoveryFlow = hasRecoveryCode || hasRecoveryToken || hasRecoveryType;
    
    console.log('[PasswordRecoveryRedirect] Recovery detection:', { 
      hasRecoveryCode, 
      hasRecoveryToken, 
      hasRecoveryType, 
      isRecoveryFlow,
      hasAnyParams,
      searchParams: Array.from(urlParams.keys()),
      hashParams: Array.from(hashParams.keys())
    });
    
    if (isRecoveryFlow) {
      console.log('[PasswordRecoveryRedirect] Recovery parameters detected on root path - redirecting to reset password');
      
      // Build the new URL with ALL recovery parameters preserved
      const resetPasswordUrl = new URL(ROUTES.RESET_PASSWORD, window.location.origin);
      
      // Copy all search parameters that might be relevant for PKCE flow
      for (const [key, value] of urlParams.entries()) {
        if (['code', 'token', 'type', 'error', 'error_code', 'error_description', 'next'].includes(key)) {
          resetPasswordUrl.searchParams.set(key, value);
        }
      }
      
      // Copy all hash parameters that might be relevant for PKCE flow
      for (const [key, value] of hashParams.entries()) {
        if (['code', 'token', 'type', 'error', 'error_code', 'error_description', 'next'].includes(key)) {
          resetPasswordUrl.searchParams.set(key, value);
        }
      }
      
      console.log('[PasswordRecoveryRedirect] Redirecting to:', resetPasswordUrl.pathname + resetPasswordUrl.search);
      navigate(resetPasswordUrl.pathname + resetPasswordUrl.search, { replace: true });
    } else if (hasAnyParams) {
      // If we have any parameters but they don't match our recovery detection,
      // wait a bit longer to see if this is a recovery flow that needs time to process
      console.log('[PasswordRecoveryRedirect] Parameters detected but not recognized as recovery - waiting before redirect');
      
      const timeoutId = setTimeout(() => {
        // Re-check after a short delay
        const delayedUrlParams = new URLSearchParams(location.search);
        const delayedHashParams = new URLSearchParams(location.hash.substring(1));
        const delayedHasRecoveryCode = delayedUrlParams.has('code') || delayedHashParams.has('code');
        const delayedHasRecoveryToken = delayedUrlParams.has('token') || delayedHashParams.has('token');
        const delayedHasRecoveryType = (delayedUrlParams.has('type') && delayedUrlParams.get('type') === 'recovery') || 
                                     (delayedHashParams.has('type') && delayedHashParams.get('type') === 'recovery');
        const delayedIsRecoveryFlow = delayedHasRecoveryCode || delayedHasRecoveryToken || delayedHasRecoveryType;
        
        if (delayedIsRecoveryFlow) {
          console.log('[PasswordRecoveryRedirect] Recovery flow detected after delay - redirecting to reset password');
          const resetPasswordUrl = new URL(ROUTES.RESET_PASSWORD, window.location.origin);
          
          // Copy all parameters
          for (const [key, value] of delayedUrlParams.entries()) {
            resetPasswordUrl.searchParams.set(key, value);
          }
          for (const [key, value] of delayedHashParams.entries()) {
            resetPasswordUrl.searchParams.set(key, value);
          }
          
          navigate(resetPasswordUrl.pathname + resetPasswordUrl.search, { replace: true });
        } else {
          // No recovery flow detected, proceed with normal redirect
          console.log('[PasswordRecoveryRedirect] No recovery flow detected after delay - redirecting to benchmarking');
          navigate(ROUTES.PERMIT_BENCHMARKING.ROOT, { replace: true });
        }
      }, 100); // Wait 100ms to see if parameters load
      
      return () => clearTimeout(timeoutId);
    } else {
      // Only redirect if we're actually on the root path
      if (location.pathname === '/') {
        console.log('[PasswordRecoveryRedirect] On root path - redirecting to benchmarking');
        console.log('[PasswordRecoveryRedirect] Navigating to:', ROUTES.PERMIT_BENCHMARKING.ROOT);
        navigate(ROUTES.PERMIT_BENCHMARKING.ROOT, { replace: true });
      }
    }
  }, [navigate, location]);
  
  return null; // This component doesn't render anything
}

export default App;